import type { ReactNode } from 'react';
import { createElement as h, Fragment } from 'react';

export type Lang = 'en' | 'zh';

/** A dictionary entry is either a plain string or a function returning JSX (for rich text). */
type Entry = { en: string; zh: string } | { en: () => ReactNode; zh: () => ReactNode };

// `b` helper for bold spans inside rich-text entries (keeps JSX terse without TSX here).
const b = (text: string) => h('b', null, text);

export const dict: Record<string, Entry> = {
  // ---------------- Header ----------------
  'nav.how': { en: 'How it works', zh: '工作原理' },
  'nav.atlas': { en: 'Atlas', zh: '全局视图' },
  'nav.faq': { en: 'FAQ', zh: '常见问题' },
  'nav.invite': { en: 'Have an invite?', zh: '有邀请码？' },
  'nav.request': { en: 'Request access', zh: '申请使用' },

  // ---------------- Hero ----------------
  'hero.badge.pill': { en: 'PRIVATE BETA', zh: '内测中' },
  'hero.badge.text': {
    en: () => h(Fragment, null, 'The ', b('Account Brain'), ' for sales teams'),
    zh: () => h(Fragment, null, '为销售团队打造的', b('客户大脑')),
  },
  'hero.h1.line1': { en: 'Account Brain for sales teams.', zh: '为销售团队打造的客户大脑。' },
  'hero.h1.line2': { en: 'Lives in your inbox.', zh: '融入你的收件箱。' },
  'hero.h1.line3': { en: 'Replaces nothing.', zh: '无需替换任何工具。' },
  'hero.lead': {
    en: "The brain your CRM was never built to be. Mindstream reads your threads, surfaces what matters, and keeps your pipeline current — without asking anyone to type into a form.",
    zh: 'CRM 从来不是为"读懂客户"而生的。客户大脑替你读完每一封客户邮件，从此刻开始读懂客户，极致服务好客户。',
  },
  'hero.cta.request': { en: 'Request access', zh: '申请使用' },
  'hero.cta.demo': { en: 'Watch demo', zh: '观看演示' },
  'hero.chip.migration': { en: 'No migration', zh: '无需迁移' },
  'hero.chip.approve': { en: 'You approve every action', zh: '每个动作你说了算' },
  'hero.chip.extension': { en: 'Chrome extension', zh: '谷歌插件' },
  'hero.chip.crm': { en: 'Fits your CRM', zh: '适配你的 CRM' },

  // ---------------- Integration strip ----------------
  'strip.label': { en: 'Designed to fit your stack', zh: '适配你正在用的工具栈' },

  // ---------------- Features ----------------
  'feat.eyebrow': { en: 'What it does', zh: '它能做什么' },
  'feat.h2.line1': { en: 'Three things it kills', zh: '装上的那一刻，' },
  'feat.h2.line2': { en: 'the moment you install it.', zh: '就帮你省掉三件事。' },
  'feat.lead': {
    en: 'Each one removes a task your day is silently full of.',
    zh: '每一件，都是你每天被悄悄占满、却又说不出口的活。',
  },
  // Card 1 — Auto-Brief
  'feat.c1.kills': {
    en: () => h(Fragment, null, 'Kills ', b('the 30-min scramble')),
    zh: () => h(Fragment, null, '省掉', b('开会前的 30 分钟临阵抱佛脚')),
  },
  'feat.c1.title': { en: 'Auto-Brief', zh: '自动简报' },
  'feat.c1.body': {
    en: 'One click and you have the full Account Brief — pulled from past threads, calendar, CRM, and open-web signals. Stakeholder map, sentiment, last momentum signal, next move. No tabs opened.',
    zh: '一键生成完整的客户简报——汇总自历史邮件、日历、CRM 和公开网络信号。决策人关系图、情绪走向、最近的进展信号、下一步动作，一应俱全。无需打开任何标签页。',
  },
  // Card 2 — CRM Sync
  'feat.c2.kills': {
    en: () => h(Fragment, null, 'Kills ', b('the data-entry tax')),
    zh: () => h(Fragment, null, '省掉', b('录入数据这笔苦差')),
  },
  'feat.c2.title': { en: 'CRM Sync', zh: 'CRM 同步' },
  'feat.c2.body': {
    en: 'Mindstream proposes structured updates from what it observed in the thread. You nod once. HubSpot or Salesforce updates. Never auto-sent — every change goes through your approval.',
    zh: 'Mindstream 根据它在邮件里观察到的内容，提出结构化的更新建议。你点头确认，CRM 随即更新。从不自动写入——每一处改动都经你批准。',
  },
  // Card 3 — Proactive Nudges
  'feat.c3.kills': {
    en: () => h(Fragment, null, 'Kills ', b('the dropped follow-up')),
    zh: () => h(Fragment, null, b('不再漏掉任何跟进')),
  },
  'feat.c3.title': { en: 'Proactive Nudges', zh: '主动跟进' },
  'feat.c3.body': {
    en: 'Short-term memory surfaces silent threads. Long-term memory surfaces dormant openings from months ago. Every nudge cites the actual thread.',
    zh: '短期记忆帮你浮现沉默已久的邮件，长期记忆唤起几个月前埋下的机会。每一条提醒都附上对应的真实邮件。',
  },
  'feat.c1.foot1': { en: 'Assembled in ~1s', zh: '约 1 秒生成' },
  'feat.c1.foot2': { en: 'Cites every source', zh: '每条都标注来源' },
  'feat.c2.foot1': { en: 'Gmail-native', zh: '原生 Gmail' },
  'feat.c2.foot2': { en: 'You approve every write', zh: '每次写入你说了算' },
  'feat.c3.foot1': { en: 'Short + long-term memory', zh: '短期 + 长期记忆' },
  'feat.c3.foot2': { en: 'Cites the thread', zh: '附上原邮件' },

  // ---------------- Atlas ----------------
  'atlas.eyebrow': { en: 'When you zoom out', zh: '当你拉远视角' },
  'atlas.h2.line1': { en: 'Same brain.', zh: '同一个大脑，' },
  'atlas.h2.line2': { en: 'Different altitude.', zh: '不同的高度。' },
  'atlas.lead': {
    en: 'You see across accounts. Two views, one engine.',
    zh: '你能纵览所有客户。两种视图，同一个引擎。',
  },
  'atlas.tab.account': { en: 'Account view', zh: '客户视图' },
  'atlas.tab.manager': { en: 'Manager dashboard', zh: '管理者看板' },
  'atlas.foot.account': {
    en: () =>
      h(Fragment, null, h('b', { style: { color: 'var(--ink)', fontWeight: 600 } }, 'Account view —'), ' the living state of every account as narrative and signal, not a field grid. Open one account and you remember everything that happened in it.'),
    zh: () =>
      h(Fragment, null, h('b', { style: { color: 'var(--ink)', fontWeight: 600 } }, '客户视图——'), '把每个客户的最新状态呈现为叙事与信号，而不是一格格的字段表。打开一个客户，你就记起了发生过的一切。'),
  },
  'atlas.foot.manager': {
    en: () =>
      h(Fragment, null, h('b', { style: { color: 'var(--ink)', fontWeight: 600 } }, 'Manager dashboard —'), ' ghost-risk accounts, reps needing attention, deals moving, handoff candidates. The truth your weekly sales meeting was trying to manufacture.'),
    zh: () =>
      h(Fragment, null, h('b', { style: { color: 'var(--ink)', fontWeight: 600 } }, '管理者看板——'), '有流失风险的客户、需要关注的销售、正在推进的商机、该交接的账户。这正是你的周会一直想拼凑出来的真相。'),
  },

  // ---------------- Two sides ----------------
  'sides.eyebrow': { en: 'What you get back', zh: '你能拿回什么' },
  'sides.h2.line1': { en: 'Two sides of the', zh: '同一份洞察，' },
  'sides.h2.line2': { en: 'same observation.', zh: '两种受益。' },
  'sides.lead': {
    en: "Same stream of conversation. Two beneficiaries. One side gets context. The other gets visibility. CRM couldn't do either. Mindstream does both.",
    zh: '同一条对话流，两类受益者。一方得到上下文，一方得到可见性。CRM 两样都给不了，Mindstream 两样都给。',
  },
  'sides.reps.tag': { en: 'For reps', zh: '给销售' },
  'sides.reps.h3': {
    en: 'Time back — and a brain that remembers every account.',
    zh: '把时间还给你，再给你一个记得住每个客户的大脑。',
  },
  'sides.mgr.tag': { en: 'For managers', zh: '给管理者' },
  'sides.mgr.h3': {
    en: 'Truth back — without taxing your reps.',
    zh: '直接听到客户的一手声音，掌控全局。',
  },
  // reps bullets
  'sides.reps.li1': {
    en: () => h(Fragment, null, 'Get back ', b('~5 hours a week'), ' from research, CRM entry, and dropped follow-ups.'),
    zh: () => h(Fragment, null, '从资料搜集、CRM 录入和漏掉的跟进里，每周', b('拿回约 5 小时'), '。'),
  },
  'sides.reps.li2': {
    en: () => h(Fragment, null, 'Every reply backed by ', b('full account context'), ', assembled before you ask for it.'),
    zh: () => h(Fragment, null, '每一次回复都有', b('完整的客户上下文'), '撑腰，且在你开口之前就备好。'),
  },
  'sides.reps.li3': {
    en: () => h(Fragment, null, 'Inherit ', b('new accounts'), ' and be caught up by lunch.'),
    zh: () => h(Fragment, null, '接手', b('新客户'), '，午饭前就能全部上手。'),
  },
  'sides.reps.li4': {
    en: () => h(Fragment, null, 'Long-term memory ', b('hands you the opening months later'), ' — right when the timing is finally ripe.'),
    zh: () => h(Fragment, null, '长期记忆', b('替你把机会递上来'), '——几个月前埋下的，正好在时机成熟时被重新提起。'),
  },
  // managers bullets
  'sides.mgr.li1': {
    en: () => h(Fragment, null, 'See what customers ', b('actually said'), ', not what reps got around to typing.'),
    zh: () => h(Fragment, null, '看到客户', b('真正说了什么'), '，而不是销售抽空敲进系统的版本。'),
  },
  'sides.mgr.li2': {
    en: () => h(Fragment, null, 'Sentiment shifts, ghost-risk accounts, stalled threads — ', b('surfaced before the deal is lost'), '.'),
    zh: () => h(Fragment, null, '情绪变化、流失风险、停滞的对话——', b('在丢单之前就浮现出来'), '。'),
  },
  'sides.mgr.li3': {
    en: () => h(Fragment, null, 'When a rep leaves, the ', b('relationship history stays'), '. No relationship reset.'),
    zh: () => h(Fragment, null, '销售离职时，', b('客户关系的历史留得下'), '。关系不必从头再来。'),
  },
  'sides.mgr.li4': {
    en: () => h(Fragment, null, 'A pipeline view that ', b('matches reality'), ', because the source is the actual thread.'),
    zh: () => h(Fragment, null, '一个', b('如实反映现状'), '的管道视图，因为数据源就是真实的邮件往来。'),
  },

  // ---------------- Scenarios ----------------
  'scn.eyebrow': { en: 'Where it earns its place', zh: '它在哪里证明自己' },
  'scn.h2.line1': { en: 'Built for the moments', zh: '为真实的' },
  'scn.h2.line2': { en: 'selling actually happens in.', zh: '销售场景而生。' },
  'scn.lead': {
    en: 'Not a persona checklist. Real situations where Mindstream pays for itself on day one.',
    zh: '不是一份人设清单，而是 Mindstream 上线第一天就能回本的真实处境。',
  },
  'scn.c1.h3': { en: '"You just inherited 12 accounts."', zh: '"你刚接手了 12 个客户。"' },
  'scn.c1.p': {
    en: 'A teammate left. A region got reshuffled. Mindstream catches you up on every account in a few minutes — full history, last momentum, who said what when. You walk into the first call already in the conversation.',
    zh: '同事离职，区域重新划分。Mindstream 几分钟就帮你补上每个客户的来龙去脉——完整历史、最近进展、谁在何时说了什么。第一通电话，你就已经在对话之中。',
  },
  'scn.c2.h3': { en: '"You\'re a founder running your own calls."', zh: '"你是亲自跑单的创始人。"' },
  'scn.c2.p': {
    en: 'No SDR briefing you. No RevOps cleaning the pipeline. Mindstream is the chief-of-staff for your customer relationships — without the chief-of-staff salary. Every thread understood, every follow-up timed.',
    zh: '没有 SDR 给你做简报，没有 RevOps 帮你清理管道。Mindstream 就是你客户关系的"幕僚长"——却不用付幕僚长的薪水。每封邮件都被读懂，每次跟进都掐准时机。',
  },
  'scn.c3.h3': { en: '"Your team uses HubSpot. Adoption is 40%."', zh: '"团队在用 HubSpot，可只有 40% 的人愿意更新。"' },
  'scn.c3.p': {
    en: "Reps don't update the CRM because updating the CRM doesn't help reps. Mindstream takes the data-entry tax off the table — the brain proposes, the rep nods, the system updates. Accuracy becomes a side effect of doing the work.",
    zh: '销售不更新 CRM，是因为更新 CRM 帮不到销售自己。Mindstream 把录入这笔苦差从桌上拿走——大脑提议、销售点头、系统更新。数据准确，成了正常干活的副产品。',
  },

  // ---------------- Compounding ----------------
  'comp.eyebrow': { en: 'Why it keeps getting better', zh: '为什么它越用越好' },
  'comp.h2.line1': { en: 'The brain you build today', zh: '你对客户的理解，' },
  'comp.h2.line2': { en: 'is the brain you wake up', zh: '会随时间' },
  'comp.h2.line3': { en: 'to next quarter.', zh: '不断复利。' },
  'comp.lead': {
    en: 'Mindstream is a compounding asset. The longer it observes, the sharper its inference.',
    zh: 'Mindstream 是一项会复利的资产。它观察得越久，判断就越准。',
  },
  'comp.curve.day1': { en: 'Day one', zh: '第一天' },
  'comp.curve.decay': { en: 'Other tools ⟶ decay', zh: '其他工具 ⟶ 衰减' },
  'comp.curve.compound': { en: 'Mindstream ⟶ compounds', zh: 'Mindstream ⟶ 复利' },
  'comp.dim1.word': { en: 'Efficiency', zh: '效率' },
  'comp.dim1.p': {
    en: 'Every conversation already understood. Every reply already partially drafted. The cognitive cost of selling drops month over month.',
    zh: '每段对话都已被读懂，每封回复都已起好草稿。销售的心智成本逐月下降。',
  },
  'comp.dim2.word': { en: 'Capacity', zh: '产能' },
  'comp.dim2.p': {
    en: 'Your team manages more accounts at higher quality. Not by hiring — by removing the manual layer between rep and customer.',
    zh: '团队能以更高质量管理更多客户。靠的不是招人，而是拿掉销售与客户之间那层手工活。',
  },
  'comp.dim3.word': { en: 'Effectiveness', zh: '效果' },
  'comp.dim3.p': {
    en: 'Every customer touch is backed by the full context Mindstream has been collecting. The right thing, said at the right time, more often.',
    zh: '每一次客户接触，都有 Mindstream 持续积累的完整上下文支撑。对的话，在对的时机，说得更频繁。',
  },
  'comp.dim4.word': { en: 'Continuity', zh: '延续性' },
  'comp.dim4.p': {
    en: "When a rep leaves, the relationship doesn't reset. The brain keeps the history, the patterns, the trust signals. The new rep walks in up to speed.",
    zh: '销售离职时，关系不必归零。大脑保留着历史、规律和信任信号，新销售一上手就跟得上。',
  },

  // ---------------- FAQ ----------------
  'faq.eyebrow': { en: 'Questions', zh: '常见问题' },
  'faq.h2.line1': { en: 'Common', zh: '常见' },
  'faq.h2.line2': { en: 'questions.', zh: '问题。' },
  'faq.talk.pre': { en: 'Still unsure? ', zh: '还有疑问？' },
  'faq.talk.link': { en: 'Talk to us →', zh: '联系我们 →' },
  // Q: install (new)
  'faq.q.install': { en: 'How do I install it?', zh: '怎么安装？' },
  'faq.a.install': {
    en: () => h(Fragment, null, 'Mindstream is a ', b('Chrome extension'), '. Install it from the Chrome Web Store and it appears as a side panel right next to your Gmail — no separate app, no new tab to live in.'),
    zh: () => h(Fragment, null, 'Mindstream 是一个 ', b('谷歌插件'), '。从 Chrome 应用商店装上后，它会作为侧边栏出现在你的 Gmail 旁边——不用切换到别的应用，也不用再开一个新标签页。'),
  },
  'faq.q.crm_leave': { en: 'Do I need to leave HubSpot or Salesforce?', zh: '我需要放弃 HubSpot 或 Salesforce 吗？' },
  'faq.a.crm_leave': {
    en: () => h(Fragment, null, 'No. Mindstream is ', b('additive'), ' — your CRM stays, your data stays where it is. Mindstream is an inference layer on top, never a replacement.'),
    zh: () => h(Fragment, null, '不需要。Mindstream 是', b('叠加式'), '的——你的 CRM 照旧，数据也留在原处。Mindstream 是它之上的一层理解，绝不是替代品。'),
  },
  'faq.q.send': { en: 'Does Mindstream send emails for me?', zh: 'Mindstream 会替我发邮件吗？' },
  'faq.a.send': {
    en: () => h(Fragment, null, 'No. Mindstream drafts replies and follow-ups for your review. ', b('You stay in control'), ' of every outbound message.'),
    zh: () => h(Fragment, null, '不会。Mindstream 只起草回复和跟进，交给你过目。', b('每一封发出的邮件都由你掌控'), '。'),
  },
  'faq.q.access': { en: 'What Gmail access do you need?', zh: '你们需要哪些 Gmail 权限？' },
  'faq.a.access': {
    en: () => h(Fragment, null, 'Mindstream reads the threads you bring us and can prepare follow-up ', b('drafts'), ' in Gmail for your review. ', b('It never sends email automatically'), ' — you stay in control of every send.'),
    zh: () => h(Fragment, null, 'Mindstream 读取你交给我们的邮件，并能在 Gmail 里为你准备跟进', b('草稿'), '供你过目。', b('它从不自动发送邮件'), '——每次发送都由你说了算。'),
  },
  'faq.q.crm_which': { en: 'Which CRMs do you sync with?', zh: '你们支持同步哪些 CRM？' },
  'faq.a.crm_which': {
    en: () => h(Fragment, null, 'HubSpot today, with more on the way. Mindstream is designed to fit your CRM, not replace it. ', b('No CRM at all also works'), ' — you still get the inbox brain.'),
    zh: () => h(Fragment, null, '目前支持 HubSpot，更多正在路上。Mindstream 的设计是适配你的 CRM，而非取代它。', b('完全不用 CRM 也没问题'), '——你照样拥有这颗收件箱里的大脑。'),
  },
  'faq.q.store': { en: 'Do you store my emails?', zh: '你们会存储我的邮件吗？' },
  'faq.a.store': {
    en: () => h(Fragment, null, b('Encrypted at rest. Never used for shared model training.'), ' You can disconnect with one click and we forget you.'),
    zh: () => h(Fragment, null, b('静态加密。绝不用于共享模型训练。'), '你可以一键断开连接，我们随即把你遗忘。'),
  },
  'faq.q.vs': { en: 'How is Mindstream different from Day.ai or Rox?', zh: 'Mindstream 跟 Day.ai 或 Rox 有什么不同？' },
  'faq.a.vs': {
    en: () => h(Fragment, null, 'Day.ai is AI-native but asks you to leave your CRM. Rox is no-migration but Salesforce-and-Snowflake-enterprise only. ', b('Mindstream is no-migration, individual install, and works from day one.'), ' No IT project.'),
    zh: () => h(Fragment, null, 'Day.ai 是 AI 原生的，但要你离开现有 CRM。Rox 无需迁移，却只服务 Salesforce + Snowflake 的大型企业。', b('Mindstream 无需迁移、可个人安装、第一天就能用。'), '不用立项、不用 IT 团队。'),
  },
  'faq.q.cost': { en: 'How much does it cost?', zh: '价格是多少？' },
  'faq.a.cost': {
    en: () => h(Fragment, null, 'Free during private beta. After: ', b('per-account, not per-seat.'), " We charge for the customer relationships under Brain, not the headcount you've hired."),
    zh: () => h(Fragment, null, '内测期间免费。之后：', b('按客户数计费，而非按席位。'), '我们按大脑所管理的客户关系收费，而不是按你雇了多少人。'),
  },
  'faq.q.who': { en: 'Who is Mindstream best for?', zh: 'Mindstream 最适合谁？' },
  'faq.a.who': {
    en: () => h(Fragment, null, 'B2B sales teams whose work lives in conversations — AEs, founders running their own sales, CS leaders, and RevOps tired of babysitting CRM hygiene.'),
    zh: () => h(Fragment, null, '工作就发生在对话里的 B2B 销售团队——客户经理、亲自跑单的创始人、客户成功负责人，以及厌倦了天天伺候 CRM 整洁度的 RevOps。'),
  },

  // ---------------- CTA ----------------
  'cta.eyebrow': { en: 'Private beta', zh: '内测中' },
  'cta.h2.line1': { en: 'Every customer,', zh: '让每个客户' },
  'cta.h2.line2': { en: 'truly understood.', zh: '都被真正读懂。' },
  'cta.p': {
    en: "Mindstream is invite-only while we onboard in waves. Have a code? Enter it. If not, request access and we'll bring you in.",
    zh: '请输入邀请码，或申请使用。',
  },
  'cta.tab.request': { en: 'Request access', zh: '申请使用' },
  'cta.tab.invite': { en: 'Have an invite code', zh: '我有邀请码' },
  'cta.email.placeholder': { en: 'you@company.com', zh: 'you@company.com' },
  'cta.request.btn': { en: 'Request access →', zh: '申请使用 →' },
  'cta.request.sending': { en: 'Sending…', zh: '提交中…' },
  'cta.invite.placeholder': { en: 'Enter invite code', zh: '输入邀请码' },
  'cta.invite.btn': { en: 'Continue →', zh: '继续 →' },
  'cta.invite.help.pre': { en: 'No code yet? ', zh: '还没有邀请码？' },
  'cta.invite.help.link': { en: 'Request access →', zh: '申请使用 →' },
  'cta.chip.free': { en: 'Free during beta', zh: '内测免费' },
  'cta.chip.cancel': { en: 'Cancel anytime', zh: '随时取消' },
  'cta.chip.gmail': { en: 'Gmail-native', zh: '原生 Gmail' },
  'cta.chip.migration': { en: 'No migration', zh: '无需迁移' },

  // ---------------- Footer ----------------
  'footer.copy.pre': { en: '© 2026 Mindstream · ', zh: '© 2026 Mindstream · ' },
  'footer.privacy': { en: 'Privacy Policy', zh: '隐私政策' },
  'footer.copy.post': { en: ' · All rights reserved', zh: ' · 保留所有权利' },

  // ---------------- Document title ----------------
  'doc.title': {
    en: 'Mindstream — Account Brain for sales teams',
    zh: 'Mindstream — 为销售团队打造的客户大脑',
  },
};
